import { LogRecord, LogContext } from '../models';
import { v4 as uuidv4 } from 'uuid';
import { METADATA } from '../constants';
import { redactComprehensive } from './redact-sensitive-data.util';


/**
 * Transforms console arguments into structured LogRecord format
 */
export function formatLogRecord(
  level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG',
  args: any[],
  context: LogContext
): LogRecord {
  const { message, data } = parseConsoleArguments(args);
  
  // Redact sensitive data from both message and data using comprehensive redaction
  const redactedMessage = typeof message === 'string' ? 
    redactComprehensive({ msg: message }).msg : 
    redactComprehensive(message);
  const redactedData = data !== undefined ? redactComprehensive(data) : undefined;
  
  return {
    level,
    message: redactedMessage,
    class: "WriteLoggingService",
    method: "writeLogsBatch",
    thread: 'main',
    timestamp: new Date().toISOString(),
    data: redactedData,
    kinective: {
      product: METADATA.PRODUCT_NAME,
      environment: context.environment,
      service: METADATA.getServiceName(context.environment),
      correlationId: uuidv4(),
      partnerName: METADATA.PARTNER_NAME,
      partnerId: context.partnerId,
      externalId: context.externalId,
      institutionName: context.institution,
      institutionId: context.assetId,
      sessionId: context.sessionId
    },
    user: context.externalUserId
  };
}

/**
 * Parses console arguments to extract message and data
 * Supports Node.js-style format strings (%s, %d, %j, %o)
 */
function parseConsoleArguments(
  args: any[]
): { message: string; data: any } {
  if (args.length === 0) {
    return { message: '', data: undefined };
  }

  const [firstArg, ...rest] = args;

  // Non-string first argument: treat as structured data
  if (typeof firstArg !== 'string') {
    return {
      message: serializeToString(firstArg),
      data: rest.length === 1 ? rest[0] : rest.length > 1 ? rest : undefined
    };
  }

  // Check for format specifiers
  const matches = firstArg.match(/%[sdjo%]/g);

  if (!matches) {
    return {
      message: firstArg,
      data: rest.length === 1 ? rest[0] : rest.length > 1 ? rest : undefined
    };
  }

  let message = '';
  const data: any[] = [];
  let argIndex = 0;

  message = firstArg.replace(/%[sdjo%]/g, (token) => {
    if (token === '%%') {
      return '%';
    }

    if (argIndex >= rest.length) {
      return token;
    }

    const value = rest[argIndex++];

    const safeNumber = (value: unknown): string => {
      try {
        return String(Number(value));
      } catch {
        return serializeToString(value);
      }
    };

    const safeJson = (value: unknown, pretty = false): string => {
      try {
        return JSON.stringify(value, null, pretty ? 2 : undefined);
      } catch {
        return serializeToString(value);
      }
    };

    switch (token) {
      case '%s':
        return serializeToString(value);
      case '%d':
        return safeNumber(value);
      case '%j':
        return safeJson(value);
      case '%o':
        return safeJson(value, true);
      default:
        return token;
    }
  });

  // Remaining args become structured data
  if (argIndex < rest.length) {
    const remainingData = rest.slice(argIndex);
    data.push(...remainingData);
  }

  return { 
    message, 
    data: data.length === 1 ? data[0] : data.length > 1 ? data : undefined
  };
}

/**
 * Safely serializes any value to a string representation
 * Handles objects, arrays, primitives, and circular references
 */
function serializeToString(value: any): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  
  // Handle primitive types
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  
  // Handle functions
  if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;
  
  // Handle Date objects
  if (value instanceof Date) return value.toISOString();
  
  // Handle RegExp objects
  if (value instanceof RegExp) return value.toString();
  
  // Handle Error objects
  if (value instanceof Error) {
    return JSON.stringify({
      name: value.name,
      message: value.message,
      stack: value.stack
    });
  }
  
  // Handle objects and arrays
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    // Handle circular references or other JSON.stringify errors
    try {
      return JSON.stringify(value, getCircularReplacer(), 2);
    } catch (fallbackError) {
      return `[Object: ${Object.prototype.toString.call(value)}]`;
    }
  }
}

/**
 * Replacer function for JSON.stringify to handle circular references
 */
function getCircularReplacer() {
  const seen = new WeakSet();
  return (key: string, value: any) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    return value;
  };
}
